import {useEffect, useState} from 'react'
import styled, {css, keyframes} from 'styled-components'

const STATUS_URL =
    'https://raw.githubusercontent.com/schief-ai/system-status/refs/heads/main/status.json'

type Severity = 'warning' | 'danger' | 'info' | 'solid-danger'

interface SystemStatus {
    active: boolean
    message: string
    severity?: Severity
}

const SEVERITY_STYLES: Record<Severity, ReturnType<typeof css>> = {
    warning: css`
        background: #fef3c7;
        color: #92400e;
        border-bottom: 1px solid rgba(146, 64, 14, 0.18);
    `,
    danger: css`
        background: #fee2e2;
        color: #7f1d1d;
        border-bottom: 1px solid rgba(122, 14, 16, 0.18);
    `,
    info: css`
        background: #dbeafe;
        color: #1e3a5f;
        border-bottom: 1px solid rgba(12, 68, 124, 0.18);
    `,
    'solid-danger': css`
        background: #dc2626;
        color: #ffffff;
        border-bottom: 1px solid #991b1b;
    `,
}

const pulseAnimation = keyframes`
    0%   { transform: scale(0.6); opacity: 0.5; }
    100% { transform: scale(2);   opacity: 0;   }
`

const Bar = styled.div<{$severity: Severity}>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 24px;
    font-size: 14px;
    line-height: 1.4;
    font-family: 'Roboto', sans-serif;
    flex-shrink: 0;
    ${({$severity}) => SEVERITY_STYLES[$severity]}
`

const IconWrap = styled.span`
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    display: grid;
    place-items: center;
    svg { width: 18px; height: 18px; }
`

const Pulse = styled.span`
    flex: 0 0 auto;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: currentColor;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.4;
        animation: ${pulseAnimation} 1.8s ease-out infinite;
    }
`

const Text = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    strong { font-weight: 600; }
    span { opacity: 0.85; }
`

const CloseButton = styled.button<{$solid: boolean}>`
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    color: inherit;
    background: transparent;
    border: 0;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.15s ease, background 0.15s ease;

    &:hover {
        opacity: 1;
        background: ${({$solid}) =>
            $solid ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.06)'};
    }

    svg { width: 16px; height: 16px; }
`

const ICONS: Record<'warning' | 'danger' | 'info', JSX.Element> = {
    warning: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
    ),
    danger: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    ),
    info: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
    ),
}

export default function SystemStatusBanner() {
    const [status, setStatus] = useState<SystemStatus>({active: false, message: ''})
    const [dismissed, setDismissed] = useState(false)

    useEffect(() => {
        fetch(`${STATUS_URL}?t=${Date.now()}`)
            .then((res) => res.json())
            .then(setStatus)
            .catch(() => {})
    }, [])

    if (!status.active || dismissed) return null

    const severity = status.severity ?? 'warning'
    const isSolid = severity === 'solid-danger'
    const showPulse = severity === 'solid-danger' || severity === 'warning'
    const iconKey = severity === 'solid-danger' ? 'danger' : severity === 'info' ? 'info' : 'warning'
    const canDismiss = severity !== 'solid-danger' && severity !== 'danger'

    return (
        <Bar $severity={severity} role={isSolid ? 'alert' : 'status'} aria-live={isSolid ? 'assertive' : 'polite'}>
            {showPulse ? <Pulse /> : <IconWrap>{ICONS[iconKey]}</IconWrap>}
            <Text>
                <strong>{status.message}</strong>
            </Text>
            {canDismiss && (
                <CloseButton $solid={isSolid} aria-label="Dispensar aviso" onClick={() => setDismissed(true)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </CloseButton>
            )}
        </Bar>
    )
}
